/**
 * Координатная плоскость для тренажёра plane15.
 * Данные: границы, линия {k,b}, shade above|below, regions[[[x,y]...]], guides.
 */
function intersectSegLine(p1, p2, k, b) {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const d1 = y1 - k * x1 - b;
  const d2 = y2 - k * x2 - b;
  if (Math.abs(d1 - d2) < 1e-12) return null;
  const t = d1 / (d1 - d2);
  if (t < -1e-9 || t > 1 + 1e-9) return null;
  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
}

function halfPlanePolygon(k, b, side, bounds) {
  const { xMin, xMax, yMin, yMax } = bounds;
  const corners = [
    [xMin, yMin],
    [xMax, yMin],
    [xMax, yMax],
    [xMin, yMax],
  ];
  const eps = 1e-9;
  const pred = (x, y) => {
    const lineY = k * x + b;
    return side === 'above' ? y >= lineY - eps : y <= lineY + eps;
  };
  const out = [];
  for (let i = 0; i < 4; i += 1) {
    const cur = corners[i];
    const prev = corners[(i + 3) % 4];
    const curIn = pred(cur[0], cur[1]);
    const prevIn = pred(prev[0], prev[1]);
    if (curIn) {
      if (!prevIn) {
        const hit = intersectSegLine(prev, cur, k, b);
        if (hit) out.push(hit);
      }
      out.push(cur);
    } else if (prevIn) {
      const hit = intersectSegLine(prev, cur, k, b);
      if (hit) out.push(hit);
    }
  }
  return out;
}

function lineSegment(k, b, bounds) {
  const { xMin, xMax, yMin, yMax } = bounds;
  const raw = [];
  const add = (x, y) => {
    if (x >= xMin - 1e-6 && x <= xMax + 1e-6 && y >= yMin - 1e-6 && y <= yMax + 1e-6) {
      raw.push([x, y]);
    }
  };
  add(xMin, k * xMin + b);
  add(xMax, k * xMax + b);
  if (Math.abs(k) > 1e-9) {
    add((yMin - b) / k, yMin);
    add((yMax - b) / k, yMax);
  }
  const uniq = [];
  raw.forEach((p) => {
    if (!uniq.some((q) => Math.abs(q[0] - p[0]) < 1e-6 && Math.abs(q[1] - p[1]) < 1e-6)) {
      uniq.push(p);
    }
  });
  if (uniq.length < 2) return null;
  return [uniq[0], uniq[uniq.length - 1]];
}

function ticks(min, max) {
  const start = Math.ceil(min + 1e-9);
  const end = Math.floor(max - 1e-9);
  const out = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}

export default function TrainerPlaneSvg({
  plane,
  compact = false,
  className = '',
}) {
  if (!plane) return null;

  const width = plane.width || 340;
  const height = plane.height || 300;
  const xMin = Number(plane.x_min);
  const xMax = Number(plane.x_max);
  const yMin = Number(plane.y_min);
  const yMax = Number(plane.y_max);
  if (![xMin, xMax, yMin, yMax].every(Number.isFinite) || xMax <= xMin || yMax <= yMin) {
    return null;
  }

  const pad = compact ? 22 : 28;
  const bounds = { xMin, xMax, yMin, yMax };
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;

  const toPx = (x, y) => [
    pad + ((x - xMin) / xSpan) * (width - 2 * pad),
    height - pad - ((y - yMin) / ySpan) * (height - 2 * pad),
  ];

  const polyPoints = (pts) => pts.map(([x, y]) => toPx(x, y).join(',')).join(' ');

  const xTicks = ticks(xMin, xMax);
  const yTicks = ticks(yMin, yMax);
  const [ox, oy] = toPx(0, 0);
  const line = plane.line;
  const seg = line && Number.isFinite(line.k) && Number.isFinite(line.b)
    ? lineSegment(Number(line.k), Number(line.b), bounds)
    : null;
  const shadePoly = line && (plane.shade === 'above' || plane.shade === 'below')
    ? halfPlanePolygon(Number(line.k), Number(line.b), plane.shade, bounds)
    : [];

  const plot = {
    x: pad,
    y: pad,
    w: width - 2 * pad,
    h: height - 2 * pad,
  };

  return (
    <svg
      className={`trainer-plane-svg ${compact ? 'is-compact' : ''} ${className}`.trim()}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={compact ? 168 : 260}
      role="img"
      aria-label="Координатная плоскость"
    >
      <rect
        x={plot.x}
        y={plot.y}
        width={plot.w}
        height={plot.h}
        className="trainer-plane-plot"
      />
      {xTicks.map((n) => {
        const [x] = toPx(n, 0);
        return (
          <line
            key={`vg-${n}`}
            x1={x}
            y1={plot.y}
            x2={x}
            y2={plot.y + plot.h}
            className={`trainer-plane-grid ${n === 0 ? 'is-axis' : ''}`}
          />
        );
      })}
      {yTicks.map((n) => {
        const [, y] = toPx(0, n);
        return (
          <line
            key={`hg-${n}`}
            x1={plot.x}
            y1={y}
            x2={plot.x + plot.w}
            y2={y}
            className={`trainer-plane-grid ${n === 0 ? 'is-axis' : ''}`}
          />
        );
      })}
      {shadePoly.length >= 3 && (
        <polygon points={polyPoints(shadePoly)} className="trainer-plane-shade" />
      )}
      {(plane.regions || []).map((poly, idx) => (
        poly?.length >= 3 ? (
          <polygon
            key={`reg-${idx}`}
            points={polyPoints(poly)}
            className="trainer-plane-region"
          />
        ) : null
      ))}
      {(plane.guides || []).map((g, idx) => {
        if (g.kind === 'vline' && Number.isFinite(g.x)) {
          const [x] = toPx(g.x, 0);
          return (
            <line
              key={`g-${idx}`}
              x1={x}
              y1={plot.y}
              x2={x}
              y2={plot.y + plot.h}
              className="trainer-plane-guide"
            />
          );
        }
        if (g.kind === 'hline' && Number.isFinite(g.y)) {
          const [, y] = toPx(0, g.y);
          return (
            <line
              key={`g-${idx}`}
              x1={plot.x}
              y1={y}
              x2={plot.x + plot.w}
              y2={y}
              className="trainer-plane-guide"
            />
          );
        }
        if (g.kind === 'diag') {
          const d = lineSegment(1, 0, bounds);
          if (!d) return null;
          const [a, c] = d;
          const [x1, y1] = toPx(a[0], a[1]);
          const [x2, y2] = toPx(c[0], c[1]);
          return (
            <line
              key={`g-${idx}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className="trainer-plane-guide"
            />
          );
        }
        return null;
      })}
      {seg && (() => {
        const [a, c] = seg;
        const [x1, y1] = toPx(a[0], a[1]);
        const [x2, y2] = toPx(c[0], c[1]);
        return (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className="trainer-plane-line"
          />
        );
      })()}
      {xTicks.filter((n) => n !== 0).map((n) => {
        const [x] = toPx(n, 0);
        return (
          <text
            key={`xt-${n}`}
            x={x}
            y={Math.min(height - 6, oy + 14)}
            className="trainer-plane-tick"
            textAnchor="middle"
          >
            {n}
          </text>
        );
      })}
      {yTicks.filter((n) => n !== 0).map((n) => {
        const [, y] = toPx(0, n);
        return (
          <text
            key={`yt-${n}`}
            x={Math.max(8, ox - 8)}
            y={y + 4}
            className="trainer-plane-tick"
            textAnchor="end"
          >
            {n}
          </text>
        );
      })}
      {xMin < 0 && xMax > 0 && yMin < 0 && yMax > 0 && (
        <text x={ox + 8} y={oy + 14} className="trainer-plane-tick">
          0
        </text>
      )}
    </svg>
  );
}
